import React from 'react';
import { Box, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import type { GalleryItem, PendingTileState } from '../types';
import GalleryTile from './GalleryTile';
import PendingAnalysisTile from './PendingAnalysisTile';
import { useInfiniteScroll } from '@/hooks/common/customHooks';

type SortBy = 'newest' | 'oldest' | 'pet_name';
type StatusFilter = 'all' | 'completed' | 'failed' | 'pending';

interface Props {
  galleryItems: GalleryItem[];
  galleryLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  pendingTile: PendingTileState | null;
  sortBy: SortBy;
  statusFilter: StatusFilter;
  userEmailFilter: string;
  availableUsers: string[];
  onSortChange: (sort: SortBy) => void;
  onStatusFilterChange: (filter: StatusFilter) => void;
  onUserEmailFilterChange: (email: string) => void;
  onItemClick: (item: GalleryItem) => void;
  onPendingTileClick: () => void;
}

const ParsnipGallery: React.FC<Props> = ({
  galleryItems,
  galleryLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  pendingTile,
  sortBy,
  statusFilter,
  userEmailFilter,
  availableUsers,
  onSortChange,
  onStatusFilterChange,
  onUserEmailFilterChange,
  onItemClick,
  onPendingTileClick,
}) => {
  useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore || galleryLoading,
    onLoadMore,
    threshold: 200,
  });

  return (
    <Box sx={{ paddingX: '70px', paddingY: 4 }}>
      {/* Header: title + sort/filter controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
              sx={{
                fontFamily: 'Maven Pro',
                fontSize: '14px',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F39A81' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F39A81' },
              }}
            >
              <MenuItem value="all" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>All Status</MenuItem>
              <MenuItem value="completed" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Completed</MenuItem>
              <MenuItem value="failed" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Failed</MenuItem>
              <MenuItem value="pending" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Pending</MenuItem>
            </Select>
          </FormControl>

          {/* User Filter */}
          {availableUsers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel shrink sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Filter by User</InputLabel>
              <Select
                value={userEmailFilter}
                label="Filter by User"
                notched
                displayEmpty
                onChange={(e) => onUserEmailFilterChange(e.target.value)}
                sx={{
                  fontFamily: 'Maven Pro',
                  fontSize: '14px',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#959BEA' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#959BEA' },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>All Users</MenuItem>
                {availableUsers.map((email) => (
                  <MenuItem key={email} value={email} sx={{ fontFamily: 'Maven Pro', fontSize: '13px' }}>
                    {email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Sort By */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => onSortChange(e.target.value as SortBy)}
              sx={{
                fontFamily: 'Maven Pro',
                fontSize: '14px',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#959BEA' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#959BEA' },
              }}
            >
              <MenuItem value="newest" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Newest first</MenuItem>
              <MenuItem value="oldest" sx={{ fontFamily: 'Maven Pro', fontSize: '14px' }}>Oldest first</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Loading state */}
      {galleryLoading && !pendingTile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#959BEA' }} />
        </Box>
      )}

      {/* Empty state */}
      {!galleryLoading && galleryItems.length === 0 && !pendingTile && (
        <Typography sx={{ fontFamily: 'Maven Pro', color: '#999', textAlign: 'center', py: 4 }}>
          {statusFilter === 'all' 
            ? 'No analyses yet. Upload videos and click Analyse to get started!'
            : `No ${statusFilter} analyses found. Try changing the filter.`}
        </Typography>
      )}

      {/* Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(280px, 1fr))', gap: 3 }}>
        {pendingTile && (
          <PendingAnalysisTile
            key={pendingTile.id}
            pending={pendingTile}
            onClick={onPendingTileClick}
          />
        )}
        {galleryItems.map((item) => (
          <GalleryTile
            key={item.id}
            item={item}
            onClick={() => onItemClick(item)}
          />
        ))}
      </Box>

      {/* Load more indicator */}
      {isLoadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#959BEA' }} size={28} />
        </Box>
      )}
    </Box>
  );
};

export default ParsnipGallery;
